import React, {createElement} from 'react'

import invariant from 'invariant'
import {ConnectedProps} from 'react-redux'
import {TransitionGroup} from 'react-transition-group'

import * as modalAction from '../../actions/modal'
import {State} from '../../reducers'

import {connect} from '../../../../commons/src/services/loop'

import {ModalConfig, ModalConfigOpened, ModalContentType} from '../../types'

import ModalPortal from '../../../../commons/src/components/modal-portal'

import AuthChangePasswordModal from './auth-change-password'
import ConfirmDeleteModal from './confirm-delete'
import ConfirmDeleteMessageModal from './confirm-delete-message'
import ConfirmToggleAvailabilityModal from './confirm-toggle-availability'
import ContactModal from './contact'
import ContactFormModal from './contact-form'
import CreateDynamicProductionTimeModal from './dynamic-production-times/create'
import EditDiscountsModal from './edit-discounts'
import FatalErrorModal from './fatal-error'
import FinishGroupModal from './finish-group'
import CreateInfillModal from './infills/create'
import EditInfillsModal from './infills/edit'
import LoadingModal from './loading'
import MaterialModal from './material'
import AddTrackingModal from './orders/add-tracking'
import AdditionalCostModal from './orders/additional-cost'
import PartialRefundDetails from './orders/partial-refund-details'
import RequestRefundModal from './orders/request-refund'
import CreatePricingModal from './pricings/create'
import EditPricingsModal from './pricings/edit'
import CreateProductionTimeModal from './production-times/create'
import EditProductionTimesModal from './production-times/edit'
import EditProviderEmailsModal from './provider-email/edit'
import AddProviderInfoDimensionsModal from './provider-info/add-dimensions'
import AddProviderInfoDynamicProductionTimeModal from './provider-info/add-dynamic-production-time'
import AddProviderInfoPricingModal from './provider-info/add-pricing'
import AddProviderInfoPrintingMethodModal from './provider-info/add-printing-method'
import AddProviderInfoProductionTimesModal from './provider-info/add-production-times'
import SuggestImmediateEditingModal from './provider-info/suggest-immediate-editing'
import CreateShippingsModal from './shippings/create'
import EditShippingsModal from './shippings/edit'
import SimplteConfirmModal from './simple-confirm'
import SimpleContentModal from './simple-content'

const modals: Record<
  ModalContentType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {component: React.ComponentType<any>; portalProps?: ModalConfigOpened}
> = {
  [ModalContentType.FATAL_ERROR]: {component: FatalErrorModal},
  [ModalContentType.AUTH_CHANGE_PASSWORD]: {component: AuthChangePasswordModal},
  [ModalContentType.MATERIAL]: {component: MaterialModal},
  [ModalContentType.FINISH_GROUP]: {component: FinishGroupModal},
  [ModalContentType.CREATE_PRICING]: {component: CreatePricingModal},
  [ModalContentType.CREATE_DYNAMIC_PRODUCTION_TIME]: {component: CreateDynamicProductionTimeModal},
  [ModalContentType.EDIT_PRICINGS]: {component: EditPricingsModal},
  [ModalContentType.EDIT_DISCOUNTS]: {component: EditDiscountsModal},
  [ModalContentType.CREATE_SHIPPINGS]: {component: CreateShippingsModal},
  [ModalContentType.EDIT_SHIPPINGS]: {component: EditShippingsModal},
  [ModalContentType.CREATE_INFILL]: {component: CreateInfillModal},
  [ModalContentType.EDIT_INFILLS]: {component: EditInfillsModal},
  [ModalContentType.EDIT_PROVIDER_EMAILS]: {component: EditProviderEmailsModal},
  [ModalContentType.CREATE_PRODUCTION_TIME]: {component: CreateProductionTimeModal},
  [ModalContentType.EDIT_PRODUCTION_TIMES]: {component: EditProductionTimesModal},
  [ModalContentType.CONFIRM_DELETE]: {component: ConfirmDeleteModal},
  [ModalContentType.CONFIRM_TOGGLE_AVAILABILITY]: {component: ConfirmToggleAvailabilityModal},
  [ModalContentType.ADD_PROVIDER_INFO_DIMENSIONS]: {component: AddProviderInfoDimensionsModal},
  [ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES]: {
    component: AddProviderInfoProductionTimesModal,
  },
  [ModalContentType.ADD_PROVIDER_INFO_PRICING]: {component: AddProviderInfoPricingModal},
  [ModalContentType.ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME]: {
    component: AddProviderInfoDynamicProductionTimeModal,
  },
  [ModalContentType.ADD_PROVIDER_INFO_PRINTING_METHOD]: {
    component: AddProviderInfoPrintingMethodModal,
  },
  [ModalContentType.PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING]: {
    component: SuggestImmediateEditingModal,
  },
  [ModalContentType.CONTACT]: {component: ContactModal},
  [ModalContentType.CONTACT_FORM]: {component: ContactFormModal},
  [ModalContentType.CONFIRM_DELETE_MESSAGE]: {component: ConfirmDeleteMessageModal},
  [ModalContentType.SIMPLE_CONTENT]: {component: SimpleContentModal},
  [ModalContentType.SIMPLE_CONFIRM]: {component: SimplteConfirmModal},
  [ModalContentType.ADDITIONAL_COST]: {component: AdditionalCostModal},
  [ModalContentType.LOADING]: {component: LoadingModal},
  [ModalContentType.ENFORCE_TRACKING]: {component: AddTrackingModal},
  [ModalContentType.REQUEST_REFUND]: {component: RequestRefundModal},
  [ModalContentType.PARTIAL_REFUND_DETAILS]: {component: PartialRefundDetails},
}

const mapStateToProps = (state: State) => ({
  modalConfig: state.modal.modalConfig,
  isOpen: state.modal.isOpen,
})

const mapDispatchToProps = {
  closeModal: modalAction.close,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const getContent = (modalConfig: ModalConfig, closeModal: Props['closeModal']) => {
  if (modalConfig === null) {
    return <div />
  }

  const {contentType, contentProps, isCloseable} = modalConfig

  invariant(contentType in modals, `Unknown modal contentType "${contentType}"`)

  return (
    <ModalPortal
      {...(modals[contentType].portalProps || {})}
      key={contentType}
      onClose={isCloseable ? () => closeModal() : undefined}
    >
      {createElement(modals[contentType].component, {
        ...contentProps,
        meta: {isCloseable, ...modals[contentType].portalProps},
      })}
    </ModalPortal>
  )
}

const Modal = ({isOpen, modalConfig, closeModal}: Props) => (
  <TransitionGroup>{isOpen && getContent(modalConfig, closeModal)}</TransitionGroup>
)

export default connector(Modal)
